import React, { useState, useMemo, useEffect } from 'react';
import axiosInstance from './axiosInstance';
import './styles/OfflineLabelsAdmin.css';
import ProtectedComponent from './ProtectedComponent';

function OfflineLabelsAdmin() {
  // Form inputs
  const [company, setCompany] = useState('');
  const [store, setStore] = useState('');

  // Data & UI state
  const [labels, setLabels] = useState([]);
  const [gatewaySummary, setGatewaySummary] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [fetchedAt, setFetchedAt] = useState('');
  const [totalElements, setTotalElements] = useState(0);
  const [totalApiPages, setTotalApiPages] = useState(0);
  const [loadedPages, setLoadedPages] = useState(new Set());

  // Progress tracking
  const [progress, setProgress] = useState({ fetched: 0, total: 0, percentage: 0 });
  const [progressMessage, setProgressMessage] = useState('');

  // Table sorting
  const [sortField, setSortField] = useState('labelCode');
  const [sortDirection, setSortDirection] = useState('asc');

  // Filtering
  const [filterGateway, setFilterGateway] = useState('');
  const [filterLabelCode, setFilterLabelCode] = useState('');

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(500);

  // AI Analysis state
  const [showAiModal, setShowAiModal] = useState(false);
  const [aiAnalyzing, setAiAnalyzing] = useState(false);
  const [aiResult, setAiResult] = useState('');
  const [aiError, setAiError] = useState('');

  // AI Sample size configuration
  const [aiSampleSize, setAiSampleSize] = useState(100);
  const [aiMaxGateways, setAiMaxGateways] = useState('all');
  const [showAiConfig, setShowAiConfig] = useState(false);

  // Gateway Modal state
  const [showGatewayModal, setShowGatewayModal] = useState(false);
  const [selectedGateway, setSelectedGateway] = useState(null);
  const [gatewayLabels, setGatewayLabels] = useState([]);

  // Fetch first page only (lazy loading)
  const handleFetch = async () => {
    if (!company.trim() || !store.trim()) {
      setError('Please enter both Company Code and Store Code');
      return;
    }

    setLoading(true);
    setError('');
    setLabels([]);
    setGatewaySummary({});
    setFetchedAt('');
    setTotalElements(0);
    setTotalApiPages(0);
    setLoadedPages(new Set());
    setCurrentPage(1);

    try {
      const token = document.cookie.split('; ').find(row => row.startsWith('jwt='))?.split('=')[1];

      const response = await fetch('/api/offline-labels/fetch-page', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': token ? `Bearer ${token}` : ''
        },
        credentials: 'include',
        body: JSON.stringify({
          company: company.trim(),
          store: store.trim(),
          page: 0
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to fetch labels');
      }

      const data = await response.json();

      setLabels(data.labels || []);
      setTotalElements(data.totalElements || 0);
      setTotalApiPages(data.totalPages || 1);
      setLoadedPages(new Set([0]));
      setFetchedAt(new Date().toISOString());

      // Build gateway summary for first page
      const summary = {};
      (data.labels || []).forEach(label => {
        const gateway = label.gateway?.name || 'Unknown';
        summary[gateway] = (summary[gateway] || 0) + 1;
      });
      setGatewaySummary(summary);

      setError('');
    } catch (err) {
      console.error('Fetch error:', err);
      setError(err.message || 'Failed to fetch offline labels. Please check your credentials and try again.');
    } finally {
      setLoading(false);
    }
  };

  // Handle sorting
  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  // Filtered & sorted labels
  const processedLabels = useMemo(() => {
    let filtered = [...labels];

    // Apply filters
    if (filterGateway) {
      filtered = filtered.filter(label => {
        const gatewayName = label.gateway?.name || '';
        return gatewayName.toLowerCase().includes(filterGateway.toLowerCase());
      });
    }

    if (filterLabelCode) {
      filtered = filtered.filter(label => {
        const labelCode = label.labelCode || '';
        return labelCode.toLowerCase().includes(filterLabelCode.toLowerCase());
      });
    }

    // Sort
    filtered.sort((a, b) => {
      let aVal, bVal;

      switch (sortField) {
        case 'labelCode':
          aVal = a.labelCode || '';
          bVal = b.labelCode || '';
          break;
        case 'gateway':
          aVal = a.gateway?.name || '';
          bVal = b.gateway?.name || '';
          break;
        case 'type':
          aVal = a.type || '';
          bVal = b.type || '';
          break;
        case 'batteryLevel':
          aVal = parseFloat(a.batteryLevel || 0);
          bVal = parseFloat(b.batteryLevel || 0);
          break;
        case 'lastConnectionTime':
          aVal = a.lastConnectionTime || '';
          bVal = b.lastConnectionTime || '';
          break;
        default:
          return 0;
      }

      if (aVal < bVal) return sortDirection === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });

    return filtered;
  }, [labels, sortField, sortDirection, filterGateway, filterLabelCode]);

  // Paginated labels
  const paginatedLabels = useMemo(() => {
    const startIndex = (currentPage - 1) * pageSize;
    const endIndex = startIndex + pageSize;
    return processedLabels.slice(startIndex, endIndex);
  }, [processedLabels, currentPage, pageSize]);

  // Total pages calculation
  // If all data is loaded, use client-side pagination
  // Otherwise, use totalElements from API
  const totalPages = totalElements > 0
    ? Math.ceil(totalElements / pageSize)
    : Math.ceil(processedLabels.length / pageSize);

  // Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [filterGateway, filterLabelCode]);

  // Lazy load a page if not already loaded
  const loadPageIfNeeded = async (page) => {
    const apiPage = page - 1; // Convert to 0-indexed

    if (loadedPages.has(apiPage)) {
      return; // Already loaded
    }

    try {
      const token = document.cookie.split('; ').find(row => row.startsWith('jwt='))?.split('=')[1];

      const response = await fetch('/api/offline-labels/fetch-page', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': token ? `Bearer ${token}` : ''
        },
        credentials: 'include',
        body: JSON.stringify({
          company: company.trim(),
          store: store.trim(),
          page: apiPage
        })
      });

      if (!response.ok) {
        throw new Error('Failed to load page');
      }

      const data = await response.json();

      // Merge new labels into existing array
      setLabels(prevLabels => {
        const newLabels = [...prevLabels];
        const startIndex = apiPage * 500;
        data.labels.forEach((label, idx) => {
          newLabels[startIndex + idx] = label;
        });
        return newLabels;
      });

      // Update gateway summary
      setGatewaySummary(prevSummary => {
        const newSummary = { ...prevSummary };
        (data.labels || []).forEach(label => {
          const gateway = label.gateway?.name || 'Unknown';
          newSummary[gateway] = (newSummary[gateway] || 0) + 1;
        });
        return newSummary;
      });

      // Mark page as loaded
      setLoadedPages(prev => new Set([...prev, apiPage]));
    } catch (err) {
      console.error('Failed to load page:', err);
    }
  };

  // Pagination handlers
  const goToPage = async (page) => {
    const targetPage = Math.max(1, Math.min(page, totalPages));
    setCurrentPage(targetPage);
    await loadPageIfNeeded(targetPage);
  };

  const handlePageSizeChange = (newSize) => {
    setPageSize(newSize);
    setCurrentPage(1);
  };

  // Export to CSV - Include ALL fields dynamically
  const handleExportCSV = () => {
    if (processedLabels.length === 0) {
      alert('No data to export');
      return;
    }

    // Get all unique keys from all labels
    const allKeys = new Set();
    processedLabels.forEach(label => {
      Object.keys(label).forEach(key => allKeys.add(key));
      // Include nested gateway fields
      if (label.gateway) {
        Object.keys(label.gateway).forEach(key => allKeys.add(`gateway.${key}`));
      }
    });

    const headers = Array.from(allKeys);

    const rows = processedLabels.map(label => {
      return headers.map(header => {
        if (header.startsWith('gateway.')) {
          const gatewayKey = header.split('.')[1];
          return label.gateway?.[gatewayKey] || '';
        }
        const value = label[header];
        if (typeof value === 'object' && value !== null) {
          return JSON.stringify(value);
        }
        return value || '';
      });
    });

    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.map(cell => `"${cell}"`).join(','))
    ].join('\n');

    // Download
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `offline_labels_${company}_${store}_${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };

  // Load all remaining pages (for AI analysis)
  const loadAllPages = async () => {
    const pagesToLoad = [];
    for (let i = 0; i < totalApiPages; i++) {
      if (!loadedPages.has(i)) {
        pagesToLoad.push(i);
      }
    }

    if (pagesToLoad.length === 0) {
      return; // Everything already loaded
    }

    try {
      const token = document.cookie.split('; ').find(row => row.startsWith('jwt='))?.split('=')[1];

      // Load pages in parallel
      const promises = pagesToLoad.map(apiPage =>
        fetch('/api/offline-labels/fetch-page', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': token ? `Bearer ${token}` : ''
          },
          credentials: 'include',
          body: JSON.stringify({
            company: company.trim(),
            store: store.trim(),
            page: apiPage
          })
        }).then(res => res.json())
      );

      const results = await Promise.all(promises);

      // Merge all results
      const allNewLabels = [];
      results.forEach((data, idx) => {
        const apiPage = pagesToLoad[idx];
        const startIndex = apiPage * 500;
        data.labels.forEach((label, labelIdx) => {
          allNewLabels[startIndex + labelIdx] = label;
        });
      });

      // Merge with existing labels
      setLabels(prevLabels => {
        const merged = [...prevLabels];
        allNewLabels.forEach((label, idx) => {
          if (label) merged[idx] = label;
        });
        return merged;
      });

      // Update loaded pages
      setLoadedPages(prev => {
        const updated = new Set(prev);
        pagesToLoad.forEach(page => updated.add(page));
        return updated;
      });
    } catch (err) {
      console.error('Failed to load all pages:', err);
    }
  };

  // AI Preset configurations
  const handleAiPreset = async (preset) => {
    switch (preset) {
      case 'quick':
        setAiSampleSize('50');
        setAiMaxGateways('3');
        break;
      case 'deep':
        setAiSampleSize('250');
        setAiMaxGateways('5');
        break;
      case 'full':
        setAiSampleSize('all');
        setAiMaxGateways('all');
        // For full analysis, load all pages first
        await loadAllPages();
        break;
      default:
        break;
    }
    setShowAiConfig(false);
    setTimeout(() => handleAiAnalysis(), 100);
  };

  // AI Analysis handler
  const handleAiAnalysis = async () => {
    if (processedLabels.length === 0) {
      alert('No data to analyze');
      return;
    }

    setShowAiModal(true);
    setAiAnalyzing(true);
    setAiResult('');
    setAiError('');

    try {
      // Smart sampling: distribute labels across gateways
      let sampleData = [];

      if (aiMaxGateways === 'all') {
        // Include labels from all gateways, up to sampleSize
        const actualSize = aiSampleSize === 'all' ? processedLabels.length : Math.min(parseInt(aiSampleSize), processedLabels.length);
        sampleData = processedLabels.slice(0, actualSize);
      } else {
        // Group labels by gateway
        const gatewayGroups = {};
        processedLabels.forEach(label => {
          const gatewayName = label.gateway?.name || 'Unknown';
          if (!gatewayGroups[gatewayName]) {
            gatewayGroups[gatewayName] = [];
          }
          gatewayGroups[gatewayName].push(label);
        });

        // Get top N gateways by label count
        const maxGateways = parseInt(aiMaxGateways);
        const sortedGateways = Object.entries(gatewayGroups)
          .sort(([, a], [, b]) => b.length - a.length)
          .slice(0, maxGateways);

        // Calculate labels per gateway
        const totalSampleSize = aiSampleSize === 'all' ? processedLabels.length : parseInt(aiSampleSize);
        const labelsPerGateway = Math.ceil(totalSampleSize / sortedGateways.length);

        // Collect samples from each gateway
        sortedGateways.forEach(([gatewayName, labels]) => {
          sampleData.push(...labels.slice(0, labelsPerGateway));
        });

        // Trim to exact sample size if needed
        if (aiSampleSize !== 'all') {
          sampleData = sampleData.slice(0, parseInt(aiSampleSize));
        }
      }

      // Format sample data for API
      const formattedSample = sampleData.map(label => ({
        labelCode: label.labelCode,
        gateway: label.gateway?.name || 'Unknown',
        type: label.type,
        firmware: label.firmwareVersion || label.firmware || label.fwVersion,
        batteryLevel: label.batteryLevel,
        lastConnectionTime: label.lastResponseTime || label.lastConnectionTime,
        networkStatus: label.networkStatus
      }));

      const token = document.cookie.split('; ').find(row => row.startsWith('jwt='))?.split('=')[1];

      const response = await fetch('/api/offline-labels/ai-analysis-stream', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': token ? `Bearer ${token}` : ''
        },
        credentials: 'include',
        body: JSON.stringify({
          sampleData: formattedSample,
          question: 'Analyze these offline ESL labels and identify patterns, root causes, and provide actionable recommendations.',
          context: {
            company,
            store,
            totalLabels: labels.length,
            filteredLabels: processedLabels.length,
            sampleSize: formattedSample.length,
            maxGateways: aiMaxGateways
          }
        })
      });

      if (!response.ok) {
        throw new Error('Failed to start AI analysis');
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      let fullResult = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() || '';

        for (const line of lines) {
          if (line.startsWith('data: ')) {
            const data = JSON.parse(line.substring(6));

            if (data.type === 'content') {
              fullResult += data.text;
              setAiResult(fullResult);
            } else if (data.type === 'complete') {
              setAiResult(data.result);
              console.log(`AI Analysis complete - Cost: $${data.cost?.toFixed(4)}, Duration: ${data.duration_ms}ms`);
            } else if (data.type === 'error') {
              setAiError(data.error);
            }
          }
        }
      }
    } catch (err) {
      console.error('AI Analysis error:', err);
      setAiError(err.message || 'Failed to analyze data');
    } finally {
      setAiAnalyzing(false);
    }
  };

  // Sort indicator
  const SortIndicator = ({ field }) => {
    if (sortField !== field) return <span className="sort-indicator">⇅</span>;
    return <span className="sort-indicator">{sortDirection === 'asc' ? '↑' : '↓'}</span>;
  };

  // Handle gateway card click
  const handleGatewayClick = (gateway) => {
    const filtered = labels.filter(label => {
      const gatewayName = label.gateway?.name || 'Unknown';
      return gatewayName === gateway;
    });
    setGatewayLabels(filtered);
    setSelectedGateway(gateway);
    setShowGatewayModal(true);
  };

  return (
    <ProtectedComponent functionCode="SA02" functionName="Offline Labels Admin">
      <div className="offline-labels-admin">
      {/* Loading Overlay */}
      {loading && (
        <div className="loading-overlay">
          <div className="loading-spinner-container">
            <div className="loading-spinner"></div>
            <p className="loading-text">Fetching offline labels...</p>
            <p className="loading-subtext">Please wait while we retrieve the data</p>
          </div>
        </div>
      )}

      <div className="header">
        <h1>Offline Labels Analysis</h1>
      </div>

      {/* Input Form */}
      <div className="input-section">
        <div className="input-group">
          <label htmlFor="company">Company Code:</label>
          <input
            id="company"
            type="text"
            value={company}
            onChange={(e) => setCompany(e.target.value)}
            placeholder="e.g., WFC"
            disabled={loading}
          />
        </div>

        <div className="input-group">
          <label htmlFor="store">Store Code:</label>
          <input
            id="store"
            type="text"
            value={store}
            onChange={(e) => setStore(e.target.value)}
            placeholder="e.g., 594"
            disabled={loading}
          />
        </div>

        <button
          className="fetch-button"
          onClick={handleFetch}
          disabled={loading}
        >
          {loading ? 'Fetching...' : 'Fetch Offline Labels'}
        </button>

        {labels.length > 0 && (
          <>
            <button className="export-button-top" onClick={handleExportCSV}>
              📥 Export CSV
            </button>

            <button className="ai-analysis-button" onClick={() => setShowAiConfig(true)}>
              🤖 AI Assistant
            </button>
          </>
        )}
      </div>

      {/* Progress Bar */}
      {loading && (
        <div className="progress-section">
          <div className="progress-message">{progressMessage}</div>
          {progress.total > 0 && (
            <>
              <div className="progress-bar-container">
                <div
                  className="progress-bar-fill"
                  style={{ width: `${progress.percentage}%` }}
                />
              </div>
              <div className="progress-stats">
                {progress.fetched.toLocaleString()} / {progress.total.toLocaleString()} labels ({progress.percentage}%)
              </div>
            </>
          )}
        </div>
      )}

      {/* Error Message */}
      {error && (
        <div className="error-message">
          <strong>Error:</strong> {error}
        </div>
      )}

      {/* Gateway Summary */}
      {Object.keys(gatewaySummary).length > 0 && (
        <div className="summary-section">
          <h2>Gateway Summary</h2>
          <div className="summary-grid">
            {Object.entries(gatewaySummary)
              .sort(([, a], [, b]) => b - a)
              .map(([gateway, count]) => (
                <div
                  key={gateway}
                  className="summary-card summary-card-clickable"
                  onClick={() => handleGatewayClick(gateway)}
                >
                  <div className="summary-gateway">{gateway}</div>
                  <div className="summary-count">{count} labels</div>
                </div>
              ))}
          </div>
          <div className="summary-total">
            <strong>Total Offline Labels:</strong> {totalElements.toLocaleString() || labels.length.toLocaleString()}
            {totalElements > labels.length && (
              <span className="summary-loaded-note">
                {' '}• Showing first {labels.length.toLocaleString()} labels
              </span>
            )}
          </div>
          {fetchedAt && (
            <div className="summary-fetched">
              Fetched at: {new Date(fetchedAt).toLocaleString()}
            </div>
          )}
        </div>
      )}

      {/* Filters & Export */}
      {labels.length > 0 && (
        <>
          <div className="controls-section">
            <div className="filters">
              <input
                type="text"
                placeholder="Filter by Gateway..."
                value={filterGateway}
                onChange={(e) => setFilterGateway(e.target.value)}
                className="filter-input"
              />
              <input
                type="text"
                placeholder="Filter by Label Code..."
                value={filterLabelCode}
                onChange={(e) => setFilterLabelCode(e.target.value)}
                className="filter-input"
              />
              {(filterGateway || filterLabelCode) && (
                <button
                  className="clear-filters"
                  onClick={() => {
                    setFilterGateway('');
                    setFilterLabelCode('');
                  }}
                >
                  Clear Filters
                </button>
              )}
            </div>

            {showAiConfig && (
              <div className="ai-modal-overlay" onClick={() => setShowAiConfig(false)}>
                <div className="ai-modal-content" onClick={(e) => e.stopPropagation()}>
                  <div className="ai-modal-header">
                    <h3>🤖 AI Analysis Options</h3>
                    <button className="ai-modal-close" onClick={() => setShowAiConfig(false)}>×</button>
                  </div>

                  <div className="ai-modal-body">
                    <div className="ai-preset-options">
                      <button className="ai-preset-option" onClick={() => handleAiPreset('quick')}>
                        <span className="ai-preset-icon">⚡</span>
                        <div className="ai-preset-text">
                          <span className="ai-preset-title">Quick Analysis</span>
                          <span className="ai-preset-desc">50 labels from top 3 gateways</span>
                        </div>
                      </button>

                      <button className="ai-preset-option" onClick={() => handleAiPreset('deep')}>
                        <span className="ai-preset-icon">🔍</span>
                        <div className="ai-preset-text">
                          <span className="ai-preset-title">Deep Dive</span>
                          <span className="ai-preset-desc">250 labels from top 5 gateways</span>
                        </div>
                      </button>

                      <button className="ai-preset-option" onClick={() => handleAiPreset('full')}>
                        <span className="ai-preset-icon">📊</span>
                        <div className="ai-preset-text">
                          <span className="ai-preset-title">Full Analysis</span>
                          <span className="ai-preset-desc">All labels from all gateways</span>
                        </div>
                      </button>
                    </div>

                    <div className="ai-custom-section">
                      <h4>Custom Analysis</h4>
                      <label>
                        Total sample size:
                        <select value={aiSampleSize} onChange={(e) => setAiSampleSize(e.target.value)} className="ai-config-select">
                          <option value="20">20 labels</option>
                          <option value="50">50 labels</option>
                          <option value="100">100 labels</option>
                          <option value="250">250 labels</option>
                          <option value="500">500 labels</option>
                          <option value="1000">1000 labels</option>
                          <option value="all">All labels</option>
                        </select>
                      </label>
                      <label>
                        Spread evenly across:
                        <select value={aiMaxGateways} onChange={(e) => setAiMaxGateways(e.target.value)} className="ai-config-select">
                          <option value="1">Top 1 gateway</option>
                          <option value="3">Top 3 gateways</option>
                          <option value="5">Top 5 gateways</option>
                          <option value="10">Top 10 gateways</option>
                          <option value="all">All gateways</option>
                        </select>
                      </label>
                      <button className="ai-run-button" onClick={handleAiAnalysis}>
                        🚀 Run Custom Analysis
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Results Table */}
          <div className="table-section">
            <div className="table-wrapper">
              <table className="labels-table">
                <thead>
                  <tr>
                    <th onClick={() => handleSort('labelCode')}>
                      Label Code <SortIndicator field="labelCode" />
                    </th>
                    <th onClick={() => handleSort('gateway')}>
                      Gateway <SortIndicator field="gateway" />
                    </th>
                    <th onClick={() => handleSort('type')}>
                      Type <SortIndicator field="type" />
                    </th>
                    <th>Firmware</th>
                    <th onClick={() => handleSort('batteryLevel')}>
                      Battery <SortIndicator field="batteryLevel" />
                    </th>
                    <th onClick={() => handleSort('lastConnectionTime')}>
                      Last Connection Date <SortIndicator field="lastConnectionTime" />
                    </th>
                    <th>Network Status</th>
                    <th>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <span>Additional Data</span>
                        <span style={{ fontSize: '0.75rem', fontWeight: 'normal', opacity: 0.8 }}>
                          {((currentPage - 1) * pageSize) + 1}-{Math.min(currentPage * pageSize, processedLabels.length)} of {processedLabels.length}
                          {processedLabels.length !== labels.length && ` (${labels.length} total)`}
                        </span>
                      </div>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {paginatedLabels.map((label, idx) => (
                    <tr key={idx}>
                      <td className="label-code">{label.labelCode}</td>
                      <td>{label.gateway?.name || 'N/A'}</td>
                      <td>{label.type || 'N/A'}</td>
                      <td>{label.firmwareVersion || label.firmware || label.fwVersion || 'N/A'}</td>
                      <td className="battery-cell">
                        {label.batteryLevel !== undefined && label.batteryLevel !== null ? (
                          <span className={`battery-level battery-${getBatteryClass(label.batteryLevel)}`}>
                            {formatBatteryLevel(label.batteryLevel)}
                          </span>
                        ) : (
                          'N/A'
                        )}
                      </td>
                      <td className="date-cell">
                        {label.lastResponseTime || label.lastConnectionTime
                          ? new Date(label.lastResponseTime || label.lastConnectionTime).toLocaleString()
                          : 'N/A'}
                      </td>
                      <td>
                        <span className="status-badge status-offline">
                          {label.networkStatus || 'OFFLINE'}
                        </span>
                      </td>
                      <td className="additional-data">
                        <details>
                          <summary>View All</summary>
                          <pre>{JSON.stringify(label, null, 2)}</pre>
                        </details>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination Controls */}
            {totalPages > 1 && (
              <div className="pagination-section">
                <div className="pagination-info">
                  Page {currentPage} of {totalPages}
                </div>

                <div className="pagination-controls">
                  <button
                    className="pagination-button"
                    onClick={() => goToPage(1)}
                    disabled={currentPage === 1}
                  >
                    First
                  </button>
                  <button
                    className="pagination-button"
                    onClick={() => goToPage(currentPage - 1)}
                    disabled={currentPage === 1}
                  >
                    Previous
                  </button>

                  {/* Page numbers */}
                  <div className="page-numbers">
                    {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                      let pageNum;
                      if (totalPages <= 5) {
                        pageNum = i + 1;
                      } else if (currentPage <= 3) {
                        pageNum = i + 1;
                      } else if (currentPage >= totalPages - 2) {
                        pageNum = totalPages - 4 + i;
                      } else {
                        pageNum = currentPage - 2 + i;
                      }
                      return (
                        <button
                          key={pageNum}
                          className={`page-number ${currentPage === pageNum ? 'active' : ''}`}
                          onClick={() => goToPage(pageNum)}
                        >
                          {pageNum}
                        </button>
                      );
                    })}
                  </div>

                  <button
                    className="pagination-button"
                    onClick={() => goToPage(currentPage + 1)}
                    disabled={currentPage === totalPages}
                  >
                    Next
                  </button>
                  <button
                    className="pagination-button"
                    onClick={() => goToPage(totalPages)}
                    disabled={currentPage === totalPages}
                  >
                    Last
                  </button>
                </div>

                <div className="page-size-selector">
                  <label>Rows per page:</label>
                  <select
                    value={pageSize}
                    onChange={(e) => handlePageSizeChange(Number(e.target.value))}
                    className="page-size-select"
                  >
                    <option value={100}>100</option>
                    <option value={250}>250</option>
                    <option value={500}>500</option>
                    <option value={1000}>1000</option>
                  </select>
                </div>
              </div>
            )}
          </div>
        </>
      )}

      {/* Empty State */}
      {!loading && labels.length === 0 && !error && (
        <div className="empty-state">
          <p>Enter a Company Code and Store Code above to fetch offline labels.</p>
        </div>
      )}

      {/* AI Analysis Loading Modal */}
      {aiAnalyzing && (
        <div className="ai-loading-overlay">
          <div className="ai-loading-container">
            <div className="ai-loading-spinner"></div>
            <h2 className="ai-loading-title">🤖 AI Analysis in Progress</h2>
            <p className="ai-loading-text">
              Analyzing {aiSampleSize === 'all' ? processedLabels.length : Math.min(parseInt(aiSampleSize), processedLabels.length)} labels...
            </p>
            <p className="ai-loading-subtext">
              This may take a minute or two depending on sample size.
            </p>
            <div className="ai-loading-details">
              <div className="ai-loading-detail-item">
                <span className="ai-loading-detail-label">Sample Size:</span>
                <span className="ai-loading-detail-value">
                  {aiSampleSize === 'all' ? 'All' : aiSampleSize} labels
                </span>
              </div>
              <div className="ai-loading-detail-item">
                <span className="ai-loading-detail-label">Gateways:</span>
                <span className="ai-loading-detail-value">
                  {aiMaxGateways === 'all' ? 'All' : `Top ${aiMaxGateways}`}
                </span>
              </div>
            </div>
            <div className="ai-loading-progress-bar">
              <div className="ai-loading-progress-fill"></div>
            </div>
          </div>
        </div>
      )}

      {/* AI Analysis Results Modal */}
      {showAiModal && !aiAnalyzing && (
        <div className="ai-modal-overlay" onClick={() => setShowAiModal(false)}>
          <div className="ai-modal" onClick={(e) => e.stopPropagation()}>
            <div className="ai-modal-header">
              <h2>📊 Intelligent Analysis</h2>
              <button
                className="ai-modal-close"
                onClick={() => setShowAiModal(false)}
              >
                ✕
              </button>
            </div>
            <div className="ai-modal-body">
              {aiError && (
                <div className="ai-error">
                  <strong>Error:</strong> {aiError}
                </div>
              )}
              {aiResult && (
                <div className="ai-result">
                  <div dangerouslySetInnerHTML={{ __html: formatMarkdown(aiResult) }} />
                </div>
              )}
            </div>
            {(aiResult || aiError) && (
              <div className="ai-modal-footer">
                {aiResult && (
                  <button
                    className="ai-download-button"
                    onClick={() => {
                      const blob = new Blob([aiResult], { type: 'text/plain' });
                      const url = URL.createObjectURL(blob);
                      const link = document.createElement('a');
                      link.href = url;
                      link.download = `analysis_${company}_${store}_${new Date().toISOString().split('T')[0]}.txt`;
                      link.click();
                      URL.revokeObjectURL(url);
                    }}
                  >
                    📥 Download Analysis (.txt)
                  </button>
                )}
                <button className="ai-close-button" onClick={() => setShowAiModal(false)}>
                  Close
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Gateway Details Modal */}
      {showGatewayModal && (
        <div className="gateway-modal-overlay" onClick={() => setShowGatewayModal(false)}>
          <div className="gateway-modal" onClick={(e) => e.stopPropagation()}>
            <div className="gateway-modal-header">
              <div>
                <h2>Gateway Details: {selectedGateway}</h2>
                <p className="gateway-modal-subtitle">
                  {gatewayLabels.length} offline {gatewayLabels.length === 1 ? 'label' : 'labels'}
                </p>
              </div>
              <button
                className="gateway-modal-close"
                onClick={() => setShowGatewayModal(false)}
              >
                ✕
              </button>
            </div>
            <div className="gateway-modal-body">
              {gatewayLabels.length === 0 ? (
                <p className="gateway-modal-empty">No labels found for this gateway.</p>
              ) : (
                <div className="gateway-modal-table-wrapper">
                  <table className="gateway-modal-table">
                    <thead>
                      <tr>
                        <th>Label Code</th>
                        <th>Type</th>
                        <th>Firmware</th>
                        <th>Battery</th>
                        <th>Last Connection</th>
                        <th>Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {gatewayLabels.map((label, idx) => (
                        <tr key={idx}>
                          <td className="label-code">{label.labelCode}</td>
                          <td>{label.type || 'N/A'}</td>
                          <td>{label.firmwareVersion || label.firmware || label.fwVersion || 'N/A'}</td>
                          <td className="battery-cell">
                            {label.batteryLevel !== undefined && label.batteryLevel !== null ? (
                              <span className={`battery-level battery-${getBatteryClass(label.batteryLevel)}`}>
                                {formatBatteryLevel(label.batteryLevel)}
                              </span>
                            ) : (
                              'N/A'
                            )}
                          </td>
                          <td className="date-cell">
                            {label.lastResponseTime || label.lastConnectionTime
                              ? new Date(label.lastResponseTime || label.lastConnectionTime).toLocaleString()
                              : 'N/A'}
                          </td>
                          <td>
                            <span className="status-badge status-offline">
                              {label.networkStatus || 'OFFLINE'}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
            <div className="gateway-modal-footer">
              <div className="gateway-modal-stats">
                <div className="gateway-stat">
                  <span className="gateway-stat-label">Total Labels:</span>
                  <span className="gateway-stat-value">{gatewayLabels.length}</span>
                </div>
                <div className="gateway-stat">
                  <span className="gateway-stat-label">Average Battery:</span>
                  <span className="gateway-stat-value">
                    {gatewayLabels.length > 0
                      ? Math.round(
                          gatewayLabels.reduce((sum, label) => {
                            const percent = getBatteryPercent(label.batteryLevel);
                            return sum + (percent || 0);
                          }, 0) / gatewayLabels.length
                        ) + '%'
                      : 'N/A'}
                  </span>
                </div>
              </div>
              <button className="gateway-modal-close-btn" onClick={() => setShowGatewayModal(false)}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
    </ProtectedComponent>
  );
}

// Battery level lookup table
const batteryLookup = {
  'F': { percent: 85, notes: '' },
  'E': { percent: 80, notes: '' },
  'D': { percent: 75, notes: '' },
  'C': { percent: 70, notes: '' },
  'B': { percent: 65, notes: '' },
  'A': { percent: 60, notes: '' },
  '9': { percent: 55, notes: '' },
  '8': { percent: 50, notes: '' },
  '7': { percent: 45, notes: '' },
  '6': { percent: 40, notes: '' },
  '5': { percent: 35, notes: '' },
  '4': { percent: 30, notes: 'Low Battery' },
  '3': { percent: 25, notes: '' },
  '2': { percent: 20, notes: 'Not Functional' },
  '1': { percent: 15, notes: '' },
  '0': { percent: 10, notes: '' }
};

// Helper function for battery level styling
function getBatteryClass(level) {
  const percent = getBatteryPercent(level);
  if (percent === null) return 'unknown';
  if (percent >= 75) return 'high';
  if (percent >= 50) return 'medium';
  if (percent >= 25) return 'low';
  return 'critical';
}

// Helper function to get battery percentage from hex level
function getBatteryPercent(level) {
  if (!level) return null;

  // Convert to string and get last character (single hex digit)
  const levelStr = String(level).toUpperCase();
  const lastChar = levelStr.charAt(levelStr.length - 1);

  // Look up in battery table
  if (batteryLookup[lastChar]) {
    return batteryLookup[lastChar].percent;
  }

  return null;
}

// Helper function to format battery level display
function formatBatteryLevel(level) {
  const percent = getBatteryPercent(level);

  if (percent === null) {
    return 'Unknown';
  }

  return `${percent}%`;
}

// Simple markdown formatter for AI results
function formatMarkdown(markdown) {
  if (!markdown) return '';

  let html = markdown
    // Headers
    .replace(/^### (.*$)/gim, '<h3>$1</h3>')
    .replace(/^## (.*$)/gim, '<h2>$1</h2>')
    .replace(/^# (.*$)/gim, '<h1>$1</h1>')
    // Bold
    .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
    // Italic
    .replace(/\*(.*?)\*/g, '<em>$1</em>')
    // Lists
    .replace(/^\d+\.\s+(.*$)/gim, '<li>$1</li>')
    .replace(/^-\s+(.*$)/gim, '<li>$1</li>')
    // Line breaks
    .replace(/\n\n/g, '</p><p>')
    .replace(/\n/g, '<br />');

  // Wrap lists
  html = html.replace(/(<li>.*?<\/li>)/gs, '<ul>$1</ul>');

  // Wrap paragraphs
  html = '<p>' + html + '</p>';

  return html;
}

export default OfflineLabelsAdmin;
